// Transport-agnostic socket interface. Production wires to the
// browser's WebSocket; tests inject a fake. ControllerSocket talks to
// this interface only — it never touches `new WebSocket(` directly.

export type TransportEvent =
  | { type: 'open' }
  | { type: 'message'; data: string }
  | { type: 'close'; code: number; reason: string }
  | { type: 'error'; reason: string };

export interface Transport {
  readonly url: string;
  send(text: string): void;
  close(code?: number, reason?: string): void;
}

export interface TransportFactory {
  (url: string, listener: (e: TransportEvent) => void): Transport;
}

/** Default factory using the browser's WebSocket. This file is the
 *  one place in the codebase where `new WebSocket(` is allowed; the
 *  ESLint config blocks it everywhere else. */
export const browserTransportFactory: TransportFactory = (url, listener) => {
  const ws = new WebSocket(url);
  ws.addEventListener('open', () => listener({ type: 'open' }));
  ws.addEventListener('message', (ev: MessageEvent) => {
    if (typeof ev.data === 'string') listener({ type: 'message', data: ev.data });
  });
  ws.addEventListener('close', (ev: CloseEvent) =>
    listener({ type: 'close', code: ev.code, reason: ev.reason }),
  );
  ws.addEventListener('error', () =>
    listener({ type: 'error', reason: 'transport error' }),
  );
  return {
    url,
    send: (text) => ws.send(text),
    close: (code, reason) => ws.close(code, reason),
  };
};
